<!--
    The interface mode toggle, for every role.

    **Up to three modes it is a plain row of buttons**, which is what the app has
    always done and what fits: the drawer leaves about 366 px, so three cells get
    ~122 px each and every label reads. Nothing is grouped and nothing opens,
    because with three there is nothing to save.

    **At four it groups.** Four cells get ~91 px, and a Vuetify button is
    `0 16px` padding plus a 0.875 rem uppercase label — LOGISTICS needs ~116 px
    and DELIVERY ~110 px, so both clip. So Manage keeps the left half and the
    productive modes — Logistics, Production, Delivery — share the right one.
    That button shows the productive mode you are in, active; or, while you are
    in Manage, Production, inactive. The header stays 50 px whatever the count,
    and a fifth mode is one more row rather than a redesign.

    A 2 x 2 grid was tried first and abandoned: `v-btn-toggle` is a single row by
    definition (`flex-wrap: nowrap`, `overflow-y: hidden`, `height: 48px` at
    default density), so the wrapped second row was clipped out of sight.

    The group is a **`v-menu`**, which is also what makes it survive the drawer:
    a hand-rolled panel positioned inside the card was clipped away in turn,
    because `.v-card` ships `overflow: hidden`. A menu teleports out of the card
    entirely and brings outside-click, Escape and focus handling with it. Its
    width is measured off the column rather than left to Vuetify — a menu sizes
    to its content, and half the drawer is the whole point of the shape. Rows are
    56 px, the touch floor the delivery screens are built to.

    Modes the account cannot have are **hidden**, never greyed: a disabled
    control is a target that does nothing, and on a tablet it gets tapped before
    it gets read. Modes it owns but cannot use *right now* (stdlib mode,
    company-wide at a company that does not work by date) stay greyed, because
    those come back when the project selection changes.

    This replaces `ButtonModeProdWorker.vue`, which existed only because this
    component used to carry a Manage button a production worker cannot use.
    Which modes each role gets lives in `session_store.availableModes`.
-->
<template>
    <v-card v-if="show" variant="flat" class="my-2 border-thin w-100">
        <div class="mode-row" :style="{ '--cell-width': cell_width }">
            <!-- Three or fewer: one button each, no grouping, nothing to open. -->
            <template v-if="!grouped">
                <v-btn v-for="m in available" :key="m.value" class="mode-cell" height="50" rounded="0"
                    :color="m.value === current ? 'accent' : undefined"
                    :variant="m.value === current ? 'tonal' : 'text'" :disabled="m.disabled"
                    @click="pick(m)">
                    <div class="d-flex flex-column align-center">
                        <v-icon class="mb-1">{{ m.icon }}</v-icon>
                        <span class="mode-label">{{ t(m.label) }}</span>
                    </div>
                </v-btn>
            </template>

            <template v-else>
                <v-btn v-if="manage" class="mode-cell manage-cell" height="50" rounded="0"
                    :color="current === 'manage' ? 'accent' : undefined"
                    :variant="current === 'manage' ? 'tonal' : 'text'" :disabled="manage.disabled"
                    @click="pick(manage)">
                    <div class="d-flex flex-column align-center">
                        <v-icon class="mb-1">{{ manage.icon }}</v-icon>
                        <span class="mode-label">{{ t(manage.label) }}</span>
                    </div>
                </v-btn>

                <div class="prod-col" ref="colRef">
                    <v-menu v-model="open" location="bottom" offset="0" :width="menu_width"
                        @update:model-value="measure">
                        <template v-slot:activator="{ props }">
                            <v-btn v-bind="props" class="w-100" height="50" rounded="0"
                                :color="in_productive ? 'accent' : undefined"
                                :variant="in_productive ? 'tonal' : 'text'"
                                :disabled="shown_productive.disabled">
                                <div class="d-flex flex-column align-center">
                                    <v-icon class="mb-1">{{ shown_productive.icon }}</v-icon>
                                    <span class="mode-label d-flex align-center ga-1">
                                        {{ t(shown_productive.label) }}
                                        <v-icon size="16">mdi-menu-down</v-icon>
                                    </span>
                                </div>
                            </v-btn>
                        </template>

                        <v-list color="accent">
                            <v-list-item v-for="m in productive" :key="m.value" min-height="56"
                                :active="m.value === current" :disabled="m.disabled" @click="pick(m)">
                                <template v-slot:prepend>
                                    <v-icon size="20">{{ m.icon }}</v-icon>
                                </template>
                                <!-- No tick: `active` already tints the row, and
                                     the button above the menu names the same
                                     mode. A third marker for one fact is noise. -->
                                <v-list-item-title>{{ t(m.label) }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </template>
        </div>
    </v-card>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { usePitStoreBasics } from "@/stores/app_common";
import { useAppStore } from "@/stores/app";
import { useSelectedStore } from "@/stores/selected";
import { useSessionStore } from "@/stores/session";

const { t } = usePitStoreBasics();
const router = useRouter();
const app_store = useAppStore();
const selected_store = useSelectedStore();
const session_store = useSessionStore();

const MODES = {
    manage: { icon: "mdi-laptop", label: "management_mode_short" },
    logistics: { icon: "mdi-forklift", label: "logistics_mode_short" },
    production: { icon: "pit:production", label: "production_mode_short" },
    delivery: { icon: "mdi-truck-delivery-outline", label: "delivery_mode_short" },
};

// Workflow order: material in, panels built, panels out.
const PRODUCTIVE = ["logistics", "production", "delivery"];

// Where a row of buttons stops fitting. Three at ~122 px each read fine; four
// at ~91 px clip LOGISTICS and DELIVERY.
const ROW_FITS = 3;

const open = ref(false);
const current = computed(() => app_store.app_mode);

const available = computed(() => {
    const disabled = session_store.contextuallyDisabledModes;
    return session_store.availableModes.map(value => ({
        value,
        ...MODES[value],
        disabled: disabled.includes(value),
    }));
});

const grouped = computed(() => available.value.length > ROW_FITS);

/**
 * Every cell takes exactly the same width, stated rather than negotiated.
 *
 * `flex: 1 1 0` ought to be enough, but `.v-btn` ships `flex-shrink: 0` and a
 * `min-width` per size, and the grouped row mixes a button with a plain div
 * holding one. An explicit share removes the argument: two halves when Manage
 * is there, otherwise one column, and an even split of however many buttons
 * the ungrouped row shows.
 */
const cell_count = computed(() => {
    if (!grouped.value) return available.value.length || 1;
    return manage.value ? 2 : 1;
});

const cell_width = computed(() => `${100 / cell_count.value}%`);

const manage = computed(() => available.value.find(m => m.value === "manage") || null);
const productive = computed(() => available.value.filter(m => PRODUCTIVE.includes(m.value)));
const in_productive = computed(() => PRODUCTIVE.includes(current.value));

// A menu sizes to its content, so it is told the column's width instead. Read
// when it opens rather than once at mount: the drawer narrows on a phone.
const colRef = ref(null);
const menu_width = ref(undefined);
const measure = (is_open) => {
    if (is_open) menu_width.value = colRef.value?.offsetWidth;
};

// A route change from anywhere else closes it too, not only picking from it.
watch(current, () => { open.value = false; });

/**
 * Which productive mode the grouped button wears.
 *
 * The one you are in, shown active — or, while you are in Manage, Production,
 * shown inactive. Production is the default face because it is the mode the
 * shop floor is in nearly all the time, and a button that names where you would
 * land beats one that names where you were.
 *
 * The last fallback only fires for an account that has productive modes but not
 * Production — a worker on a logistics-only line — where naming Production
 * would point at a mode they cannot enter.
 */
const shown_productive = computed(() => {
    if (!productive.value.length) return null;
    return productive.value.find(m => m.value === current.value)
        || productive.value.find(m => m.value === "production")
        || productive.value[0];
});

/**
 * Hidden when there is nothing to choose — but shown anyway when the app is in
 * a mode this account does not own.
 *
 * That happens for real: the rack load page is deliberately behind no module
 * and no role guard, because it is what the QR sticker on a rack opens and
 * whoever is standing there has to be able to read it. Without this a worker
 * with a single mode would land in delivery with no visible way back.
 */
const show = computed(
    () => available.value.length > 1
        || !available.value.some(m => m.value === current.value));

/**
 * Landing route per mode.
 *
 * The manager toggle used to only set `app_mode` and let the left menu redraw,
 * while the production worker's variant routed. Routing for everyone is what
 * lets one component serve both: setting `app_mode` alone leaves a manager in
 * delivery looking at the previous mode's page.
 */
const routeFor = (mode) => {
    const project_uuid = selected_store.selected_project?.uuid;
    const in_project = project_uuid && project_uuid !== "all";
    if (mode === "logistics") {
        return in_project
            ? { name: "logistics", params: { project_uuid } }
            : { name: "company_logistics" };
    }
    if (mode === "production") return { name: "viewer" };
    if (mode === "delivery") return { name: "delivery_viewer" };
    return null;  // Manage keeps whatever manager page is open.
};

const pick = (mode) => {
    open.value = false;
    if (!mode || mode.disabled || mode.value === current.value) return;
    app_store.setMode(mode.value);
    const target = routeFor(mode.value);
    if (target) router.push(target).catch(() => { });
};
</script>

<style scoped>
.mode-row {
    display: flex;
}

/* Stated, not negotiated: `.v-btn` carries `flex-shrink: 0` and a `min-width`
   per size, and the grouped row mixes a button with a div holding one, so a
   `flex: 1 1 0` split leaves the wider content wider. `box-sizing` is border-box
   throughout, so the cell with the divider still measures the same. */
.mode-row > .mode-cell,
.mode-row > .manage-cell,
.mode-row > .prod-col {
    flex: 0 0 var(--cell-width);
    width: var(--cell-width);
    max-width: var(--cell-width);
    min-width: 0;
}

.mode-row > .mode-cell:not(:last-child),
.manage-cell {
    border-right: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.mode-label {
    font-size: 0.7rem;
    line-height: 1;
    letter-spacing: 0.04em;
}
</style>
